import { Injectable } from '@angular/core';

import { IMediaLoadingState } from '../interfaces/media.interfaces';

import { EImageState, EVideoState } from '../enums/media-loader-type.enum';

@Injectable({ providedIn: 'root' })
export class SkeletonLoaderService {
  handleImageStateChange(event: Event, image: IMediaLoadingState): Promise<void> {
    const target = event.target as HTMLImageElement;

    return new Promise(resolve => {
      if (event.type === EImageState.Error) {
        this.handleImageError(image);
        resolve();
        return;
      }

      if (event.type !== EImageState.Load) {
        resolve();
        return;
      }

      this.handleImageLoad(image, target, resolve);
    });
  }

  private handleImageError(image: IMediaLoadingState): void {
    image.isError = true;
    image.isLoaded = true;
    image.isRendered = true;
  }

  private handleImageLoad(
    image: IMediaLoadingState,
    target: HTMLImageElement,
    resolve: () => void
  ): void {
    image.isLoaded = true;

    if (!this.canDecodeImage(target)) {
      this.fallbackRender(image, resolve);
      return;
    }

    this.decodeImage(target, image, resolve);
  }

  private canDecodeImage(target: HTMLImageElement): boolean {
    return target && typeof target.decode === 'function';
  }

  private decodeImage(
    target: HTMLImageElement,
    image: IMediaLoadingState,
    resolve: () => void
  ): void {
    target
      .decode()
      .then(() => this.setRenderedAndResolve(image, resolve))
      .catch(() => this.setRenderedAndResolve(image, resolve));
  }

  private setRenderedAndResolve(image: IMediaLoadingState, resolve: () => void): void {
    image.isRendered = true;
    resolve();
  }

  private fallbackRender(image: IMediaLoadingState, resolve: () => void): void {
    setTimeout(() => this.setRenderedAndResolve(image, resolve), 100);
  }

  handleVideoStateChange(event: Event, video: IMediaLoadingState): void {
    if (event.type === EVideoState.Error) {
      this.setVideoState(video, { isError: true, isLoaded: true, isRendered: true });
      return;
    }

    // Handle multiple video events for Safari compatibility
    if (['loadedmetadata', 'loadeddata'].includes(event.type)) {
      this.setVideoState(video, { isLoaded: true });
    }

    if (['canplay', 'canplaythrough'].includes(event.type)) {
      this.setVideoState(video, { isLoaded: true, isRendered: true });
    }
  }

  private setVideoState(video: IMediaLoadingState, state: Partial<IMediaLoadingState>): void {
    Object.assign(video, state);
  }
}
